import { useState } from "react"
import {
	IconDotsOutline18 as Dots,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
	IconXmarkOutline18 as Xmark,
} from "nucleo-ui-outline-18"

import type { HelpCenterHomeSection } from "@productlane/lib/src/help-center-home-sections"
import { homeSectionLimit } from "@productlane/lib/src/help-center-home-sections"
import { IconButton, NucleoFillIcon, Popover, Tooltip } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"

import type { ArticleWithSubpages } from "./types"
import { NavSectionHeader } from "@/components/shared/nav-section-header"
import { RoleCheck } from "@/components/shared/role-check"
import { useZero } from "@/lib/zero"
import { getArticleDisplayTitle } from "./article-kind"
import { HelpCenterHomeTile } from "./help-center-home-tile"
import { HOME_SECTION_LABEL } from "./home-sections"

const SLOT_CLASS =
	"group relative flex flex-col items-stretch overflow-hidden rounded-lg border border-dashed border-gray-300 bg-black/[0.02] text-left hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-850 dark:hover:bg-gray-800"

export function AddArticleSlot({
	candidates,
	onPick,
}: {
	candidates: readonly ArticleWithSubpages[]
	onPick: (articleId: string) => void
}) {
	const [isOpen, setIsOpen] = useState(false)

	return (
		<Popover
			open={isOpen}
			onOpenChange={setIsOpen}
			align="start"
			className="w-[20rem] p-0"
			content={
				<Command>
					<CommandInput autoFocus placeholder="Search articles..." />
					<CommandList>
						<CommandEmpty>No article left to add.</CommandEmpty>
						{candidates.map((article) => (
							<CommandItem
								key={article.id}
								className="px-3"
								value={`${getArticleDisplayTitle(article)} ${article.id}`}
								Icon={() => (
									<NucleoFillIcon
										icon={article.icon}
										className="size-4 flex-none text-tertiary"
									/>
								)}
								onSelect={() => {
									setIsOpen(false)
									onPick(article.id)
								}}
							>
								{getArticleDisplayTitle(article)}
							</CommandItem>
						))}
					</CommandList>
				</Command>
			}
		>
			<button type="button" className={SLOT_CLASS}>
				<div className="border-b border-dashed border-gray-300 px-3 pb-5 pt-5 sm:pb-10 lg:px-[1.2rem] dark:border-gray-700">
					<div className="flex h-6 w-6 items-center justify-center">
						<Plus className="size-[18px] flex-none text-tertiary" />
					</div>
				</div>
				<div className="px-3 pb-5 pt-4 lg:px-[1.2rem]">
					<h3 className="line-clamp-1 min-w-0 text-ellipsis text-[0.9rem] font-medium tracking-tight text-primary">
						Add an article
					</h3>
					<p className="mt-1 line-clamp-2 text-[0.8rem] tracking-tight text-gray-500 dark:text-gray-400">
						Picks what customers see first on your docs home.
					</p>
				</div>
			</button>
		</Popover>
	)
}

export function RemoveFromHomeAction({ onRemove }: { onRemove: () => void }) {
	return (
		<RoleCheck not={["VIEWER"]}>
			<Tooltip label="Remove from home">
				<button
					type="button"
					aria-label="Remove from home"
					className="flex h-6 w-6 items-center justify-center rounded text-gray-500 opacity-0 hover:text-primary focus-visible:opacity-100 group-hover:opacity-100 dark:text-gray-400 dark:hover:text-primary"
					onClick={(event) => {
						event.stopPropagation()
						onRemove()
					}}
				>
					<Xmark className="size-4 flex-none" />
				</button>
			</Tooltip>
		</RoleCheck>
	)
}

export function HomeCuratedSection({
	section,
	articles,
	candidates,
	onRemoved,
}: {
	section: HelpCenterHomeSection
	articles: readonly ArticleWithSubpages[]
	candidates: readonly ArticleWithSubpages[]
	onRemoved: () => void
}) {
	const zero = useZero()
	const openSlots = homeSectionLimit(section) - articles.length

	return (
		<div className="mb-12">
			<NavSectionHeader
				label={HOME_SECTION_LABEL[section]}
				className="-ml-2 mb-3"
				action={
					<RoleCheck not={["VIEWER"]}>
						<DropdownMenu
							className="w-56"
							contentProps={{ align: "end" }}
							trigger={
								<IconButton
									Icon={Dots}
									variant="ghost"
									size="md"
									iconStrokeWidth={1.75}
									iconClassName="h-3 w-3"
									aria-label="Section actions"
									className="text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
									type="button"
								/>
							}
						>
							<DropdownMenuItem
								Icon={Trash}
								onClick={() => {
									onRemoved()
									void zero.mutate(
										mutators.helpCenterArticles.clearHomeSection({
											section,
											ids: articles.map((article) => article.id),
										}),
									)
								}}
							>
								Remove from home
							</DropdownMenuItem>
						</DropdownMenu>
					</RoleCheck>
				}
			/>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{articles.map((article) => (
					<HelpCenterHomeTile
						key={article.id}
						article={article}
						action={
							<RemoveFromHomeAction
								onRemove={() =>
									void zero.mutate(
										mutators.helpCenterArticles.setHomeSection({
											id: article.id,
											section: null,
										}),
									)
								}
							/>
						}
					/>
				))}
				{openSlots > 0 && (
					<RoleCheck not={["VIEWER"]}>
						<AddArticleSlot
							candidates={candidates}
							onPick={(id) =>
								void zero.mutate(
									mutators.helpCenterArticles.setHomeSection({ id, section }),
								)
							}
						/>
					</RoleCheck>
				)}
			</div>
		</div>
	)
}
